import { useState, useEffect } from 'react'

interface RadarSizeConfig {
  mobile: number
  tablet: number
  desktop: number
  large: number
}

interface RadarSizeResult {
  size: number
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
}

const DEFAULT_SIZES: RadarSizeConfig = {
  mobile: 280,   // < 640px
  tablet: 380,   // 640px - 1024px
  desktop: 500,   // > 1024px
  large: 600,     // > 1440px
}

/**
 * Hook for responsive radar sizing
 * Returns appropriate size based on viewport width
 *
 * Phase 3.1: Performance - Added debounce to prevent excessive re-renders
 */
export function useRadarSize(sizes: Partial<RadarSizeConfig> = {}): RadarSizeResult {
  const config = { ...DEFAULT_SIZES, ...sizes }

  const [size, setSize] = useState(config.desktop)
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop' | 'large'>('desktop')

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>

    const updateSize = () => {
      const width = window.innerWidth

      if (width >= 1440) {
        setSize(config.large)
        setDeviceType('large')
      } else if (width >= 1024) {
        setSize(config.desktop)
        setDeviceType('desktop')
      } else if (width >= 640) {
        setSize(config.tablet)
        setDeviceType('tablet')
      } else {
        setSize(config.mobile)
        setDeviceType('mobile')
      }
    }

    // Debounced version
    const debouncedUpdateSize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(updateSize, 150) // 150ms debounce
    }

    // Initial size
    updateSize()

    // Listen for resize with debounce
    window.addEventListener('resize', debouncedUpdateSize)

    return () => {
      window.removeEventListener('resize', debouncedUpdateSize)
      clearTimeout(timeoutId)
    }
  }, [config.mobile, config.tablet, config.desktop, config.large])

  return {
    size,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop' || deviceType === 'large',
  }
}

/**
 * Hook specifically for checking touch device
 */
export function useTouchDevice(): boolean {
  const [isTouch, setIsTouch] = useState(false)

  useEffect(() => {
    // Check for touch support
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    setIsTouch(hasTouch)
  }, [])

  return isTouch
}
